import { useCallback } from "react";
import { toast } from "react-toastify";
import {
  ensureSafeString,
  extractServerMessage,
  isAuthError,
} from "../../../infrastructure/utils/errorUtils";

/**
 * Custom hook for handling API calls with proper error handling
 * @returns {Object} - Object containing API call handlers
 */
export const useApiHandler = () => {
  /**
   * Execute an API call with proper error handling
   * @param {Function} apiCall - The API function to call
   * @param {Object} options - Configuration options
   * @param {Function} options.onSuccess - Success callback
   * @param {Function} options.onError - Custom error handler
   * @param {string} options.errorMessage - Custom error message
   * @param {Function} options.onFinally - Finally callback
   */
  const executeApi = useCallback(
    async (
      apiCall,
      {
        onSuccess,
        onError,
        errorMessage = "خطایی رخ داده است.",
        onFinally,
      } = {}
    ) => {
      try {
        const result = await apiCall();

        if (result?.isSuccess) {
          if (onSuccess) {
            onSuccess(result);
          }
          return result;
        } else {
          // Handle API-level errors (isSuccess: false)
          // Same approach as PMS_FrontEnd but with object safety
          const message = result?.message;

          if (typeof message === "string") {
            toast.error(message);
          } else if (typeof message === "object" && message?.text) {
            toast.error(message.text);
          } else {
            toast.error("خطایی رخ داده است.");
          }

          if (onError) {
            onError(result);
          }
          return result;
        }
      } catch (error) {
        // Don't show error messages for authentication errors
        if (!isAuthError(error)) {
          if (onError) {
            onError(error);
          } else {
            const serverMessage = extractServerMessage(error);
            let finalErrorMessage;

            if (serverMessage && serverMessage !== "خطایی رخ داده است.") {
              finalErrorMessage = ensureSafeString(serverMessage);
            } else {
              finalErrorMessage = ensureSafeString(errorMessage);
            }

            // Ensure we're passing a safe string to toast
            toast.error(finalErrorMessage);
          }
        }
        throw error; // Re-throw for component to handle if needed
      } finally {
        if (onFinally) {
          onFinally();
        }
      }
    },
    []
  );

  /**
   * Execute an API call and show success message
   * @param {Function} apiCall - The API function to call
   * @param {string} successMessage - Success message to show
   * @param {Object} options - Additional options
   */
  const executeApiWithSuccess = useCallback(
    async (apiCall, successMessage, options = {}) => {
      return executeApi(apiCall, {
        ...options,
        onSuccess: (result) => {
          const serverMessage = extractServerMessage(result);
          let finalSuccessMessage;

          if (serverMessage && serverMessage !== "خطایی رخ داده است.") {
            finalSuccessMessage = ensureSafeString(serverMessage);
          } else if (successMessage) {
            finalSuccessMessage = ensureSafeString(successMessage);
          }

          if (finalSuccessMessage) {
            // Ensure we're passing a safe string to toast
            toast.success(finalSuccessMessage);
          }

          if (options.onSuccess) {
            options.onSuccess(result);
          }
        },
      });
    },
    [executeApi]
  );

  return {
    executeApi,
    executeApiWithSuccess,
  };
};

export default useApiHandler;
